import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { landingPathFor } from '@/lib/landing';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get the intended destination: check query param first, then state, then default to /account
  const returnToParam = searchParams.get('returnTo');
  const fromPath = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : null;
  const explicitPath = returnToParam || searchParams.get('redirect') || searchParams.get('next') || fromPath;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Normalize email to lowercase
    const normalizedEmail = email.toLowerCase().trim();
    const result = await login(normalizedEmail, password);
    
    if (result.success) {
      navigate(landingPathFor(result.user, explicitPath, '/account'), { replace: true });
    } else {
      setError(result.error || 'Login failed. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="bg-[#050816] flex items-center justify-center px-4 py-16">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816] -z-10" />
      
      <Card className="w-full max-w-md relative z-10 bg-[#0f1724] border-white/10">
        <CardHeader className="space-y-1 text-center">
          {/* Logo */}
          <Link to="/" className="inline-block mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/a7jf2g4n_RCSTUDIOS-logo-h240.png"
              alt="RC Studios" 
              className="h-8 mx-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </Link>
          <CardTitle className="text-2xl font-light text-white tracking-wide">
            Welcome Back
          </CardTitle>
          <CardDescription className="text-[#E8E4DE]/60">
            Sign in to your RC Studios account
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#E8E4DE]/80 text-sm">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C6A14A] focus:ring-[#C6A14A]/20"
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#E8E4DE]/80 text-sm">
                  Password
                </Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-[#C6A14A] hover:text-[#D4AF5A] transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C6A14A] focus:ring-[#C6A14A]/20 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] font-medium h-11"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
            
            <p className="text-sm text-center text-[#E8E4DE]/60">
              Don't have an account?{' '}
              <Link 
                to={returnToParam ? `/register?returnTo=${encodeURIComponent(returnToParam)}` : '/register'} 
                className="text-[#C6A14A] hover:text-[#D4AF5A] transition-colors font-medium"
              >
                Create one
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
